
import { useParams, useNavigate } from "react-router-dom";
import { LessonsGrid } from "@/components/lesson/LessonsGrid";
import { LessonMode } from "@/components/lesson/LessonMode";
import { useState } from "react";
import { Lesson } from "@/types/lesson";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * ModulePage component - Displays lessons for a specific module
 * Handles lesson selection and lesson mode transitions
 */
const ModulePage = () => {
  const { topicId, moduleId } = useParams<{ topicId: string; moduleId: string }>();
  const navigate = useNavigate();
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  // Handle navigation back to the topic page
  const handleBack = () => {
    navigate(`/learn/topic/${topicId}`);
  };

  // Handle lesson selection and start lesson mode
  const handleStartLesson = async (lesson: Lesson) => {
    console.log("🚀 Starting lesson:", { lessonId: lesson.id, title: lesson.title });
    setSelectedLesson(lesson);
  };

  // Handle exiting lesson mode
  const handleExitLesson = () => {
    console.log("🏁 Exiting lesson");
    setSelectedLesson(null);
  };

  // Fetch lesson content when a lesson is selected with enhanced debugging
  const { data: lessonContent, isLoading: isLoadingLesson, error: lessonError } = useQuery({
    queryKey: ['lessonContent', selectedLesson?.id],
    queryFn: async () => {
      if (!selectedLesson?.id) {
        console.log("❌ No lesson selected for content fetch");
        return null;
      }
      
      try {
        console.log("🔍 Fetching lesson content for lesson ID:", selectedLesson.id);
        console.log("🔍 Lesson title:", selectedLesson.title);
        
        const { data, error } = await supabase
          .from('lessons')
          .select('*')
          .eq('id', selectedLesson.id)
          .maybeSingle();
        
        if (error) {
          console.error("❌ Supabase error fetching lesson content:", error);
          throw error;
        }

        if (!data) {
          console.warn("⚠️ No lesson data found for ID:", selectedLesson.id);
          return null;
        }

        console.log("✅ Raw lesson data from database:", {
          id: data.id,
          title: data.title,
          contentType: typeof data.content,
          contentLength: data.content?.length || 0,
          contentPreview: data.content ? data.content.toString().substring(0, 200) + "..." : "No content",
          hasContent: !!data.content,
          objectives: data.objectives
        });

        return data;
      } catch (error) {
        console.error("❌ Error in lesson content query for ID", selectedLesson.id, ":", error);
        toast.error("Failed to load lesson content. Please try again.");
        throw error;
      }
    },
    enabled: !!selectedLesson?.id,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Log any lesson content fetch errors
  if (lessonError) {
    console.error("❌ Lesson content query error:", lessonError);
  }

  // Ensure we have required route params
  if (!topicId || !moduleId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-red-600">Module not found.</p>
        <button 
          onClick={() => navigate("/learn")}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
        >
          Back to Learn
        </button>
      </div>
    );
  }

  // Show lesson mode if a lesson is selected and content is available
  if (selectedLesson && (lessonContent || isLoadingLesson)) {
    if (isLoadingLesson) {
      console.log("⏳ Loading lesson content...");
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    if (lessonContent) {
      console.log("✅ Rendering LessonMode with content:", {
        lessonId: lessonContent.id,
        title: lessonContent.title,
        hasContent: !!lessonContent.content,
        contentType: typeof lessonContent.content
      });
      return <LessonMode lesson={lessonContent} onExit={handleExitLesson} />;
    }

    // If we have a selected lesson but no content, show error
    console.error("❌ Selected lesson but no content available");
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load lesson content</p>
          <button 
            onClick={handleExitLesson}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }

  // Show lessons grid for the module
  return (
    <LessonsGrid 
      pathId={moduleId}
      onSelectLesson={handleStartLesson}
      onBack={handleBack}
    />
  );
};

export default ModulePage;
